'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import React, { Suspense, useState } from 'react';
import { FiHome } from 'react-icons/fi';
import { Heading } from '../shared/heading';
import { GoArrowRight } from 'react-icons/go';

import { useInfiniteQuery } from '@tanstack/react-query';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { ProductCard } from '../product/product-card';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { HtmlDescription } from '../homepage/shared/html-description';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';

type BreadcrumbsProps = {
  categoryStack: any[];
  onBreadcrumbClick: (index: number) => void;
};

const Breadcrumbs = ({ categoryStack, onBreadcrumbClick }: BreadcrumbsProps) => {
  if (!categoryStack.length) {
    return null;
  }

  return (
    <nav className="breadcrumb ml-1 px-3 sm:px-0">
      <ul className="flex items-center space-x-2 py-2">
        <li>
          <p
            className="cursor-pointer text-primary hover:underline"
            onClick={() => onBreadcrumbClick(0)}
          >
            <FiHome />
          </p>
        </li>
        {categoryStack.map((category: any, index: number) => (
          <li key={category.slug} className="flex items-center">
            <span className="ml-[2px] mr-1 cursor-default text-primary">/</span>
            <p
              className={cn('line-clamp-1 cursor-pointer text-primary', {
                'cursor-default text-foreground': index === categoryStack.length - 1,
                'hover:underline': index !== categoryStack.length - 1,
              })}
              onClick={() => onBreadcrumbClick(index + 1)}
            >
              {category.name}
            </p>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export type Category = {
  data?: Category[];
  childs: Category[];
  name: string;
  description: string;
  short_description: string;
  slug: string;
  icon?: string;
  image?: { url: string; custom_title: string; name: string };
};

type CategoriesPageNewProps = {
  categories: Category[];
  locale: string;
};

export const CategoriesPageNew = ({ categories, locale }: CategoriesPageNewProps) => {
  const [categoryStack, setCategoryStack] = useState<Category[]>([]);
  const { me } = useCustomerStore();

  const { t } = useTranslations();

  const handleCategoryClick = (category: Category) => {
    setCategoryStack((prevStack: Category[]) => [...prevStack, category]);
    window.scrollTo(0, 0);
  };

  const handleBackClick = () => {
    setCategoryStack(prevStack => prevStack.slice(0, -1));
    window.scrollTo(0, 0);
  };

  const handleBreadcrumbClick = (index: number) => {
    setCategoryStack(categoryStack.slice(0, index));
    window.scrollTo(0, 0);
  };

  const currentCategory = categoryStack.length > 0 ? categoryStack[categoryStack.length - 1] : null;
  const categoriesToDisplay = currentCategory ? currentCategory.childs : categories;

  const productsQuery = useInfiniteQuery({
    queryKey: [CacheKey.PRODUCTS, locale, me?.customer.id ?? null],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchProducts({
        locale,
        authToken: me?.authToken,
        query: {
          page: pageParam,
          results: 12,
          order: 'desc',
          orderBy: 'id',
        },
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const products = productsQuery.data?.pages.flatMap((page: any) => page.data) ?? [];

  return (
    <>
      <section className={cn('custom-section', 'my-16')}>
        <div className={cn('custom-container', 'flex flex-col items-start justify-start gap-16')}>
          <Heading title={`<strong>${t('text150')}</strong>`} />
          <HtmlDescription
            content={`<p>${t('text1834')}</p>`}
            className="w-full max-w-[1060px]"
            color="foreground"
            size="large"
          />
          <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {categories.map(category => (
              <>
                {!!category.image && (
                  <Link
                    key={category.slug}
                    href={ROUTES.CATEGORY({ slug: category.slug, page: 1 })}
                    className="col-span-1 flex w-full items-center justify-between border border-[#DEDEDED] bg-transparent p-4 text-lg font-semibold uppercase text-foreground transition-all hover:border-primary hover:text-primary sm:text-xl"
                  >
                    <Image
                      src={category.image?.url ?? '/images/placeholder.png'}
                      alt={resolveImageAlt(category?.image, category?.name)}
                      width={0}
                      height={0}
                      className="h-16 w-auto object-cover object-center"
                      sizes="100vw"
                    />
                    {category.name}
                    <GoArrowRight size={24} />
                  </Link>
                )}
              </>
            ))}
          </div>
        </div>
      </section>
      <section className={cn('custom-section', 'my-16')}>
        <div
          className={cn(
            'custom-container',
            'grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
          )}
        >
          <Suspense fallback={null}>
            {products.map(product => (
              <ProductCard product={product} key={product.slug} />
            ))}
          </Suspense>
        </div>
        {productsQuery.hasNextPage && (
          <Button
            onClick={() => productsQuery.fetchNextPage()}
            isLoading={productsQuery.isFetchingNextPage}
            className={cn('mt-16', { hidden: !productsQuery.hasNextPage })}
            variant={'outline'}
          >
            {t('text338')}
          </Button>
        )}
      </section>
    </>
  );
};
